import { useCallback, useEffect, useState } from 'react';
import { getPaddle, onPaddleEvent } from '../api/paddle';
import { PADDLE_FRAME_CLASS, PADDLE_FRAME_STYLE } from '../constants';
import { phaseForPaddleEvent } from '../model/paddle-events';
import type { CheckoutPhase, PaddleConfigData } from '../types';

type UsePaddleCheckoutResult = {
  phase: CheckoutPhase;
  openInline: (transactionId: string) => Promise<void>;
  openOverlay: (transactionId: string) => Promise<void>;
  close: () => void;
  setStarting: () => void;
  reset: () => void;
};

/** Paddle's checkout and the phase it is in. Card data stays inside Paddle's frame. */
export function usePaddleCheckout(config: PaddleConfigData | null): UsePaddleCheckoutResult {
  const [phase, setPhase] = useState<CheckoutPhase>('idle');

  useEffect(
    () =>
      onPaddleEvent((event) => {
        const next = phaseForPaddleEvent(event.name);
        if (next) setPhase(next);
      }),
    [],
  );

  const open = useCallback(
    async (transactionId: string, displayMode: 'inline' | 'overlay') => {
      if (!config) return;
      setPhase('loading');
      try {
        const paddle = await getPaddle(config);
        paddle.Checkout.open({
          transactionId,
          settings:
            displayMode === 'inline'
              ? {
                  displayMode,
                  frameTarget: PADDLE_FRAME_CLASS,
                  frameInitialHeight: 450,
                  frameStyle: PADDLE_FRAME_STYLE,
                  theme: 'dark',
                  allowLogout: false,
                  variant: 'one-page',
                }
              : { displayMode, theme: 'dark', allowLogout: false },
        });
      } catch {
        setPhase('declined');
      }
    },
    [config],
  );

  const close = useCallback(() => {
    window.Paddle?.Checkout.close();
    setPhase('idle');
  }, []);

  return {
    phase,
    openInline: (transactionId) => open(transactionId, 'inline'),
    openOverlay: (transactionId) => open(transactionId, 'overlay'),
    close,
    setStarting: () => setPhase('starting'),
    reset: () => setPhase('idle'),
  };
}
